
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle,
  User, FileUp, Star, Settings, Mail,
  FileText, PenTool, Users, ShieldCheck, Info
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

interface UserGuideContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const UserGuideContent: React.FC<UserGuideContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "profile-management") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Profile Management</h1>
          <p className="text-gray-500 mt-2">
            Learn how to set up and optimize your DGK Platform profile for maximum visibility and opportunities.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: June 25, 2023</span>
            <Badge variant="outline" className="ml-2">Essential</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="profile-importance">The Importance of Your Profile</h2>
          <p>
            Your profile is your digital identity on the DGK Platform. It's often the first impression clients and collaborators 
            have of you, and it significantly impacts your visibility in search results and AI job matching algorithms.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Profile Completion Impact
            </h3>
            <p className="text-blue-700 text-sm mb-3">
              Users with 100% complete profiles receive up to:
            </p>
            <ul className="text-sm text-blue-700 space-y-1 pl-6 list-disc">
              <li>5x more project invitations</li>
              <li>3x more profile views</li>
              <li>Higher rankings in search results</li>
              <li>Better AI job matches</li>
            </ul>
          </div>

          <h2 id="profile-sections">Profile Sections</h2>
          <p>
            Your profile consists of several key sections, each playing an important role in showcasing your skills and experience:
          </p>
          
          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="basic-info">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Basic Information
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    This section includes your name, professional title, location, and profile photo.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-sm mb-2">Best Practices:</h4>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Use a clear, professional headshot</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Choose a specific, descriptive professional title</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Include your location for local opportunity matching</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="bio-summary">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-purple-600" />
                  Bio & Summary
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    Your professional summary that highlights your unique value proposition and experience.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-sm mb-2">Writing Tips:</h4>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Keep it concise (150-300 words)</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Highlight your unique selling points</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Include relevant keywords for your industry</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Mention years of experience and notable clients</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="skills">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <PenTool className="h-5 w-5 mr-2 text-amber-600" />
                  Skills & Expertise
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    A comprehensive list of your technical and soft skills with proficiency levels.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-sm mb-2">Skill Management Tips:</h4>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Add at least 5-10 core skills</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Be honest about your proficiency levels</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Include both technical and soft skills</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Take skill assessments to verify your expertise</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="portfolio">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <FileUp className="h-5 w-5 mr-2 text-green-600" />
                  Portfolio Items
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    Showcase your best work with detailed portfolio items including images, descriptions, and results.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-sm mb-2">Portfolio Best Practices:</h4>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Showcase 3-6 high-quality examples of your best work</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Include detailed descriptions of your contribution</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Highlight measurable results and outcomes</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Use high-quality images and media</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Include testimonials when possible</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="reviews-ratings">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <Star className="h-5 w-5 mr-2 text-yellow-600" />
                  Reviews & Ratings
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    Client feedback and ratings from completed projects.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-sm mb-2">Managing Feedback:</h4>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Always request reviews after successful projects</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Respond professionally to all feedback</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Address negative feedback constructively</span>
                      </li>
                      <li className="flex items-center">
                        <Check className="h-4 w-4 text-emerald-500 mr-2" />
                        <span>Highlight top reviews in your portfolio</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <h2 id="profile-visibility">Profile Visibility Settings</h2>
          <p>
            Control who can see your profile and how it appears in search results:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Public</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Your complete profile is visible to all platform users and may appear in search engines.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <Users className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Platform Only</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Only registered DGK Platform users can view your profile.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <Mail className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Private</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Your profile is only visible to users you've connected with or invited.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-6">
            <h3 className="text-base font-medium mb-3">Profile Optimization Checklist</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                <input type="checkbox" id="check1" className="mr-2" />
                <label htmlFor="check1" className="text-sm">Upload a professional profile photo (square, at least 400x400px)</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check2" className="mr-2" />
                <label htmlFor="check2" className="text-sm">Write a compelling professional title (5-10 words)</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check3" className="mr-2" />
                <label htmlFor="check3" className="text-sm">Complete your bio with relevant keywords (150-300 words)</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check4" className="mr-2" />
                <label htmlFor="check4" className="text-sm">Add at least 5-10 relevant skills with proficiency levels</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check5" className="mr-2" />
                <label htmlFor="check5" className="text-sm">Upload 3-6 portfolio items with detailed descriptions</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check6" className="mr-2" />
                <label htmlFor="check6" className="text-sm">Verify your email and phone number</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check7" className="mr-2" />
                <label htmlFor="check7" className="text-sm">Connect your social and professional accounts</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check8" className="mr-2" />
                <label htmlFor="check8" className="text-sm">Set your availability status and work preferences</label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="check9" className="mr-2" />
                <label htmlFor="check9" className="text-sm">Complete at least one skills assessment</label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["profile-management"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("profile-management", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("profile-management", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("profile-management") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("profile-management")}
            >
              {bookmarkedArticles.includes("profile-management") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "project-creation") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Project Creation</h1>
          <p className="text-gray-500 mt-2">
            Learn how to create effective project listings that attract the right talent.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: June 28, 2023</span>
            <Badge variant="outline" className="ml-2">Client Feature</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="project-types">Project Types</h2>
          <p>
            The DGK Platform supports different project types to accommodate various needs:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Fixed-Price Projects</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Set a predefined budget for a specific deliverable or set of deliverables.
                </p>
                <div className="text-sm text-gray-700">
                  <p><strong>Best for:</strong> Projects with clearly defined scope and deliverables</p>
                  <p><strong>Payment:</strong> Released upon completion or in milestones</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Hourly Projects</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Pay freelancers based on the time spent working on your project.
                </p>
                <div className="text-sm text-gray-700">
                  <p><strong>Best for:</strong> Ongoing work or projects with evolving scope</p>
                  <p><strong>Payment:</strong> Weekly payments based on tracked hours</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Milestone-Based Projects</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Break a large project into smaller, manageable deliverables with individual payments.
                </p>
                <div className="text-sm text-gray-700">
                  <p><strong>Best for:</strong> Long-term projects that can be divided into stages</p>
                  <p><strong>Payment:</strong> Released after completion of each milestone</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Contest-Based Projects</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Receive multiple submissions and only pay for the winning entry.
                </p>
                <div className="text-sm text-gray-700">
                  <p><strong>Best for:</strong> Creative projects like logo design or naming</p>
                  <p><strong>Payment:</strong> One-time payment to the winner(s)</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="creating-effective-project">Creating an Effective Project Listing</h2>
          <p>
            Follow these steps to create a project listing that attracts the right talent:
          </p>
          
          <div className="my-6 space-y-6">
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Choose the Right Project Type</h3>
                <p className="text-gray-600 mb-3">
                  Select the project type that best fits your needs and budget structure.
                </p>
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <p className="text-gray-700 mb-2">
                    <strong>Pro Tip:</strong> For uncertain projects, start with a small hourly contract to evaluate the fit before committing to a larger project.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Write a Clear Title</h3>
                <p className="text-gray-600 mb-3">
                  Create a descriptive title that summarizes your project and includes key skills or technologies.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border border-red-200 bg-red-50 p-3 rounded-md">
                    <p className="text-sm font-medium text-red-700 mb-1">Weak Example:</p>
                    <p className="text-sm text-red-600">Need a website ASAP</p>
                  </div>
                  <div className="border border-green-200 bg-green-50 p-3 rounded-md">
                    <p className="text-sm font-medium text-green-700 mb-1">Strong Example:</p>
                    <p className="text-sm text-green-600">E-commerce Website Development with React & Shopify Integration</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Provide Detailed Requirements</h3>
                <p className="text-gray-600 mb-3">
                  Write a comprehensive description that includes project goals, specific deliverables, timeline, and technical requirements.
                </p>
                <div className="bg-gray-50 p-3 rounded-md">
                  <h4 className="font-medium text-sm mb-2">Essential Elements to Include:</h4>
                  <ul className="text-sm space-y-1">
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Project background and context</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Specific deliverables with clear success criteria</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Technical specifications and platform requirements</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Timeline expectations with key milestones</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Required skills and experience level</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Reference materials or examples</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Set a Realistic Budget</h3>
                <p className="text-gray-600 mb-3">
                  Define a budget that aligns with your project scope and attracts quality professionals.
                </p>
                <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Important:</strong> Research market rates for the skills you're seeking before setting your budget. Unrealistically low budgets attract fewer qualified professionals.
                  </p>
                  <p className="text-sm text-amber-800">
                    Use the platform's budget estimation tool to get an idea of appropriate pricing based on similar projects.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  5
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Add Screening Questions</h3>
                <p className="text-gray-600 mb-3">
                  Include specific questions for applicants to help identify the most suitable candidates.
                </p>
                <div className="bg-gray-50 p-3 rounded-md">
                  <h4 className="font-medium text-sm mb-2">Example Screening Questions:</h4>
                  <ul className="text-sm space-y-2">
                    <li>
                      <p className="font-medium">Experience Question:</p>
                      <p className="text-gray-600">Describe your experience with similar projects and share examples of your work.</p>
                    </li>
                    <li>
                      <p className="font-medium">Technical Question:</p>
                      <p className="text-gray-600">What approach would you take to implement the X feature considering our technology stack?</p>
                    </li>
                    <li>
                      <p className="font-medium">Process Question:</p>
                      <p className="text-gray-600">How do you handle client feedback and revision requests?</p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["project-creation"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("project-creation", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("project-creation", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("project-creation") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("project-creation")}
            >
              {bookmarkedArticles.includes("project-creation") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
